import { useState, useRef } from 'react';
import { transcribeAudio } from '../utils/api';

/**
 * Custom hook for handling audio recording functionality
 * @param {Function} onTranscriptionComplete - Callback function when transcription is complete
 * @returns {Object} Audio recording state and functions
 */
const useAudioRecording = (onTranscriptionComplete) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);

  /**
   * Check if audio is silent
   * @param {AudioBuffer} audioBuffer - The audio buffer to check
   * @returns {boolean} True if audio is silent
   */
  const isSilent = (audioBuffer) => {
    const rawData = audioBuffer.getChannelData(0); // Mono channel data
    const rms = Math.sqrt(
      rawData.reduce((sum, sample) => sum + sample ** 2, 0) / rawData.length
    );
    const silenceThreshold = 0.01; // Adjust based on needs
    return rms < silenceThreshold;
  };

  /**
   * Start recording audio
   */
  const startRecording = async () => {
    if (isRecording) {
      console.warn('Already recording.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new AudioContext({ sampleRate: 24000 }); // Set desired sample rate
      const recorder = new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      audioStreamRef.current = stream;

      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = null;
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);

        const tempMessageId = `temp_${Date.now()}`;
        
        if (isSilent(audioBuffer)) {
          console.warn('Silent audio detected. Skipping.');
          return;
        }

        try {
          // Send audio to the backend for transcription
          const data = await transcribeAudio(blob);
          const transcribedText = data.text || '[Transcription failed]';

          // Call the callback with the transcribed text and message ID
          onTranscriptionComplete(transcribedText, tempMessageId);
        } catch (err) {
          console.error('Error transcribing audio:', err);
          // Call the callback with an error message
          onTranscriptionComplete('[Failed to transcribe speech]', tempMessageId);
        }
      };

      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  };

  /**
   * Stop recording audio
   */
  const stopRecording = () => {
    if (!isRecording) {
      console.warn('Not recording.');
      return;
    }

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null; // Clean up reference
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null; // Clean up reference
    }

    setIsRecording(false);
  };

  return {
    isRecording,
    isAudioReady,
    setIsAudioReady,
    startRecording,
    stopRecording
  };
};

export default useAudioRecording;
